import type { AdminDebugScenario, DebugPilotMode } from '@star-snake/shared'
import type { InputState } from '../input/InputTypes'
import { mountToAppShell } from '../ui/appShell'

interface SparkCandidate {
  x: number
  z: number
  hidden?: boolean
}

interface DebugPilotContext {
  x: number
  z: number
  bearing: number
  fieldRadius: number
  portal: {
    x: number
    z: number
    active: boolean
  }
  sparks: Iterable<SparkCandidate>
  snakeHeads: Iterable<{
    x: number
    z: number
  }>
}

interface BenchmarkPhase {
  key: string
  label: string
  durationMs: number
  mode: DebugPilotMode
  scenario?: AdminDebugScenario
}

interface AdminDebugPilotOptions {
  roomName: string
  onScenario: (scenario: AdminDebugScenario) => Promise<void> | void
  onBenchmarkCompleted?: () => Promise<void> | void
}

interface BenchmarkState {
  sessionId: string
  phaseIndex: number
  phaseStartedAtMs: number
  startedAtMs: number
  scenarioTriggered: boolean
}

interface BenchmarkInfo {
  sessionId: string | null
  active: boolean
  phaseKey: string | null
  phaseLabel: string | null
  phaseIndex: number | null
  phaseCount: number | null
}

interface RuntimeRoomEconomyHistoryPoint {
  timestampMs: number
  looseWorldSparks: number
}

interface RuntimeRoomEconomyInfo {
  roomName: string
  economyDiagnostics: {
    looseWorldSparks: number
    avgVisibleSparksNearFreshSpawn: number
    conservationDeltaSparks: number
    window: {
      elapsedMs: number
      botDeaths: number
      botEvacuations: number
    }
    control: {
      roomState: string
      controlSparks: number
      freeSparks: number
      carriedSparks: number
      liquidityIntent: 'wetting' | 'holding' | 'drying'
      policyBlendLabel: string
      resolvedBehavior: {
        hunterAssignmentShare: number
        botOnBotHuntShare: number
        collectorEvacThresholdMult: number
        forcedEvacuationMaxCount: number
        forcedEvacuationMinCarriedSparks: number
        hunterProfile: string
        collectorProfile: string
      }
    }
  }
  economyHistory: RuntimeRoomEconomyHistoryPoint[]
}

const BENCHMARK_PHASES: readonly BenchmarkPhase[] = [
  { key: 'warmup', label: 'Warmup', durationMs: 12_000, mode: 'spark_collect' },
  { key: 'portal_approach', label: 'Portal Approach', durationMs: 10_000, mode: 'portal_approach' },
  { key: 'portal_orbit', label: 'Portal Orbit', durationMs: 12_000, mode: 'portal_orbit' },
  { key: 'crash_bot', label: 'Crash Bot', durationMs: 9_000, mode: 'portal_orbit', scenario: 'spawn_crash_bot' },
  { key: 'death_burst', label: 'Death Burst', durationMs: 9_000, mode: 'spark_collect', scenario: 'spawn_death_burst' },
  { key: 'cooldown', label: 'Cooldown', durationMs: 8_000, mode: 'edge_patrol' },
] as const

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function normalizeAngle(angle: number): number {
  while (angle > Math.PI) angle -= Math.PI * 2
  while (angle < -Math.PI) angle += Math.PI * 2
  return angle
}

function createSessionId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `bench-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
}

export class AdminDebugPilot {
  private readonly container: HTMLDivElement
  private readonly panel: HTMLDivElement
  private readonly statusEl: HTMLDivElement
  private readonly benchmarkBadge: HTMLDivElement
  private readonly infoBadge: HTMLDivElement
  private readonly modeButtons = new Map<DebugPilotMode, HTMLButtonElement>()
  private readonly benchmarkButton: HTMLButtonElement
  private readonly stopButton: HTMLButtonElement
  private panelOpen = false
  private active = false
  private mode: DebugPilotMode = 'off'
  private edgePatrolAngle = 0
  private benchmark: BenchmarkState | null = null
  private benchmarkCompletionTimer: number | null = null
  private runtimeInfoTimer: number | null = null

  constructor(private readonly options: AdminDebugPilotOptions) {
    this.container = document.createElement('div')
    this.container.dataset.touchIgnore = 'true'
    this.container.style.cssText = `
      position: absolute;
      left: 12px;
      bottom: 88px;
      z-index: 220;
      pointer-events: auto;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      font-family: 'Inter', sans-serif;
    `

    const toggleButton = document.createElement('button')
    toggleButton.type = 'button'
    toggleButton.dataset.touchIgnore = 'true'
    toggleButton.textContent = 'Pilot'
    toggleButton.style.cssText = this.getButtonCss(true)
    toggleButton.addEventListener('click', () => {
      this.panelOpen = !this.panelOpen
      this.panel.style.display = this.panelOpen ? 'flex' : 'none'
    })
    this.container.appendChild(toggleButton)

    this.benchmarkBadge = document.createElement('div')
    this.benchmarkBadge.dataset.touchIgnore = 'true'
    this.benchmarkBadge.style.cssText = `
      display: none;
      max-width: 220px;
      padding: 8px 10px;
      border-radius: 12px;
      background: rgba(8, 11, 21, 0.9);
      border: 1px solid rgba(255, 215, 120, 0.35);
      color: #fff8df;
      font-size: 11px;
      line-height: 1.35;
      text-align: left;
      white-space: pre-line;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
      backdrop-filter: blur(8px);
    `
    this.container.appendChild(this.benchmarkBadge)

    this.infoBadge = document.createElement('div')
    this.infoBadge.dataset.touchIgnore = 'true'
    this.infoBadge.style.cssText = `
      display: none;
      max-width: 228px;
      padding: 8px 10px;
      border-radius: 12px;
      background: rgba(8, 11, 21, 0.92);
      border: 1px solid rgba(125, 218, 255, 0.28);
      color: #e7fbff;
      font-size: 11px;
      line-height: 1.35;
      text-align: left;
      white-space: pre-line;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
      backdrop-filter: blur(8px);
    `
    this.container.appendChild(this.infoBadge)

    this.panel = document.createElement('div')
    this.panel.dataset.touchIgnore = 'true'
    this.panel.style.cssText = `
      min-width: 196px;
      display: none;
      flex-direction: column;
      gap: 6px;
      padding: 10px;
      border-radius: 14px;
      background: rgba(8, 11, 21, 0.92);
      border: 1px solid rgba(255, 215, 120, 0.35);
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
      backdrop-filter: blur(8px);
    `

    const title = document.createElement('div')
    title.textContent = 'Admin Pilot'
    title.style.cssText = 'font-size: 12px; font-weight: 700; color: #ffe083;'
    this.panel.appendChild(title)

    const benchmarkWrap = document.createElement('div')
    benchmarkWrap.style.cssText = 'display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px;'
    this.panel.appendChild(benchmarkWrap)
    this.benchmarkButton = this.createActionButton('Benchmark', async () => {
      this.startBenchmark(performance.now())
    }, true)
    this.stopButton = this.createActionButton('Stop', async () => {
      this.stopBenchmark('Benchmark stopped')
    })
    benchmarkWrap.appendChild(this.benchmarkButton)
    benchmarkWrap.appendChild(this.stopButton)

    const modeWrap = document.createElement('div')
    modeWrap.style.cssText = 'display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px;'
    this.panel.appendChild(modeWrap)
    this.addModeButton(modeWrap, 'off', 'Manual')
    this.addModeButton(modeWrap, 'spark_collect', 'Collect')
    this.addModeButton(modeWrap, 'portal_approach', 'Portal')
    this.addModeButton(modeWrap, 'portal_orbit', 'Orbit')
    this.addModeButton(modeWrap, 'edge_patrol', 'Edge')

    const scenarioWrap = document.createElement('div')
    scenarioWrap.style.cssText = 'display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px;'
    this.panel.appendChild(scenarioWrap)
    scenarioWrap.appendChild(this.createActionButton('Crash Bot', async () => this.options.onScenario('spawn_crash_bot')))
    scenarioWrap.appendChild(this.createActionButton('Death Burst', async () => this.options.onScenario('spawn_death_burst')))

    this.statusEl = document.createElement('div')
    this.statusEl.style.cssText = 'font-size: 11px; line-height: 1.35; color: rgba(255,255,255,0.86);'
    this.panel.appendChild(this.statusEl)
    this.updateStatus('Manual control')

    this.container.appendChild(this.panel)
    mountToAppShell(this.container)
    this.syncModeButtons()
    this.syncActionButtons()
    void this.refreshRuntimeInfo()
    this.runtimeInfoTimer = window.setInterval(() => {
      void this.refreshRuntimeInfo()
    }, 5000)
  }

  dispose(): void {
    if (this.benchmarkCompletionTimer !== null) {
      window.clearTimeout(this.benchmarkCompletionTimer)
      this.benchmarkCompletionTimer = null
    }
    if (this.runtimeInfoTimer !== null) {
      window.clearInterval(this.runtimeInfoTimer)
      this.runtimeInfoTimer = null
    }
    this.container.remove()
  }

  isActive(): boolean {
    return this.benchmark !== null || (this.active && this.mode !== 'off')
  }

  isBenchmarkVisible(): boolean {
    return this.benchmark !== null || this.benchmarkBadge.style.display !== 'none'
  }

  getMode(): DebugPilotMode {
    return this.getEffectiveMode()
  }

  getBenchmarkInfo(): BenchmarkInfo {
    const phase = this.benchmark ? BENCHMARK_PHASES[this.benchmark.phaseIndex] : null
    return {
      sessionId: this.benchmark?.sessionId ?? null,
      active: this.benchmark !== null,
      phaseKey: phase?.key ?? null,
      phaseLabel: phase?.label ?? null,
      phaseIndex: this.benchmark ? this.benchmark.phaseIndex + 1 : null,
      phaseCount: this.benchmark ? BENCHMARK_PHASES.length : null,
    }
  }

  tick(nowMs: number): void {
    if (!this.benchmark) return

    const phase = BENCHMARK_PHASES[this.benchmark.phaseIndex]
    if (!phase) {
      this.finishBenchmark('Benchmark complete, data uploaded')
      return
    }

    if (!this.benchmark.scenarioTriggered && phase.scenario) {
      this.benchmark.scenarioTriggered = true
      void Promise.resolve(this.options.onScenario(phase.scenario)).catch((error: unknown) => {
        const message = error instanceof Error ? error.message : String(error)
        this.updateStatus(message.slice(0, 96))
      })
    }

    const elapsedMs = nowMs - this.benchmark.phaseStartedAtMs
    const remainingMs = Math.max(0, phase.durationMs - elapsedMs)
    this.updateStatus(
      `Phase ${this.benchmark.phaseIndex + 1}/${BENCHMARK_PHASES.length}: ${phase.label} - ${Math.ceil(remainingMs / 1000)}s`,
    )
    this.showBadge(`Benchmark ${this.benchmark.phaseIndex + 1}/${BENCHMARK_PHASES.length}\n${phase.label}`)

    if (elapsedMs >= phase.durationMs) {
      this.benchmark.phaseIndex++
      this.benchmark.phaseStartedAtMs = nowMs
      this.benchmark.scenarioTriggered = false
      if (this.benchmark.phaseIndex >= BENCHMARK_PHASES.length) {
        this.finishBenchmark('Benchmark complete, data uploaded')
      }
    }
  }

  computeInput(context: DebugPilotContext): InputState | null {
    const effectiveMode = this.getEffectiveMode()
    if (effectiveMode === 'off') return null

    let baseInput: InputState | null
    switch (effectiveMode) {
      case 'spark_collect':
        baseInput = this.computeSparkCollectInput(context)
        break
      case 'portal_approach':
        baseInput = this.computePortalApproachInput(context)
        break
      case 'portal_orbit':
        baseInput = this.computePortalOrbitInput(context)
        break
      case 'edge_patrol':
        baseInput = this.computeEdgePatrolInput(context)
        break
      default:
        return null
    }

    return this.applySafetyAdjustments(context, baseInput)
  }

  private startBenchmark(nowMs: number): void {
    this.hideBadge()
    this.benchmark = {
      sessionId: createSessionId(),
      phaseIndex: 0,
      phaseStartedAtMs: nowMs,
      startedAtMs: nowMs,
      scenarioTriggered: false,
    }
    this.panelOpen = false
    this.panel.style.display = 'none'
    this.active = false
    this.mode = 'off'
    this.syncModeButtons()
    this.syncActionButtons()
    this.updateStatus(`Phase 1/${BENCHMARK_PHASES.length}: ${BENCHMARK_PHASES[0].label}`)
    this.showBadge(`Benchmark 1/${BENCHMARK_PHASES.length}\n${BENCHMARK_PHASES[0].label}`)
  }

  private stopBenchmark(message: string): void {
    this.benchmark = null
    this.active = false
    this.mode = 'off'
    this.syncModeButtons()
    this.syncActionButtons()
    this.updateStatus(message)
    this.showBadge(message, 3500)
  }

  private finishBenchmark(message: string): void {
    this.benchmark = null
    this.active = false
    this.mode = 'off'
    this.syncModeButtons()
    this.syncActionButtons()
    this.updateStatus('Benchmark complete, uploading...')
    this.showBadge('Benchmark complete, uploading...')

    const onBenchmarkCompleted = this.options.onBenchmarkCompleted
    if (!onBenchmarkCompleted) {
      this.updateStatus(message)
      this.showBadge(message, 3500)
      return
    }

    void Promise.resolve(onBenchmarkCompleted())
      .then(() => {
        this.updateStatus(message)
        this.showBadge(message, 3500)
      })
      .catch((error) => {
        const uploadError = error instanceof Error ? error.message : String(error)
        const failureText = `Benchmark complete, upload failed: ${uploadError.slice(0, 48)}`
        this.updateStatus(failureText)
        this.showBadge(failureText, 4500)
      })
  }

  private addModeButton(parent: HTMLElement, mode: DebugPilotMode, label: string): void {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.touchIgnore = 'true'
    button.textContent = label
    button.style.cssText = this.getButtonCss()
    button.addEventListener('click', () => {
      this.benchmark = null
      this.mode = mode
      this.active = mode !== 'off'
      this.syncModeButtons()
      this.syncActionButtons()
      this.updateStatus(mode === 'off' ? 'Manual control' : `Autopilot: ${label}`)
      this.hideBadge()
    })
    this.modeButtons.set(mode, button)
    parent.appendChild(button)
  }

  private createActionButton(label: string, action: () => Promise<void> | void, primary = false): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.touchIgnore = 'true'
    button.textContent = label
    button.style.cssText = this.getButtonCss(primary)
    button.addEventListener('click', async () => {
      button.disabled = true
      try {
        await action()
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        this.updateStatus(message.slice(0, 96))
      } finally {
        window.setTimeout(() => {
          button.disabled = false
          this.syncActionButtons()
        }, 300)
      }
    })
    return button
  }

  private computeSparkCollectInput(context: DebugPilotContext): InputState {
    let bestSpark: SparkCandidate | null = null
    let bestDistSq = Number.POSITIVE_INFINITY
    for (const spark of context.sparks) {
      if (spark.hidden) continue
      const dx = spark.x - context.x
      const dz = spark.z - context.z
      const distSq = dx * dx + dz * dz
      if (distSq < bestDistSq) {
        bestDistSq = distSq
        bestSpark = spark
      }
    }

    if (bestSpark) {
      const desiredHeading = Math.atan2(bestSpark.z - context.z, bestSpark.x - context.x)
      const dist = Math.sqrt(bestDistSq)
      return {
        desiredHeading,
        boost: dist > 2200,
        hasAim: true,
        strength: 1,
        distancePx: clamp(dist * 0.05, 60, 180),
      }
    }

    return this.computePortalApproachInput(context)
  }

  private computePortalApproachInput(context: DebugPilotContext): InputState {
    if (context.portal.active) {
      const dx = context.portal.x - context.x
      const dz = context.portal.z - context.z
      const distSq = dx * dx + dz * dz
      const distance = Math.sqrt(distSq)
      if (distance < 850) {
        return this.computePortalOrbitInput(context)
      }
      return {
        desiredHeading: Math.atan2(dz, dx),
        boost: distance > 2500,
        hasAim: true,
        strength: 1,
        distancePx: clamp(distance * 0.05, 60, 200),
      }
    }

    return this.computeEdgePatrolInput(context)
  }

  private computePortalOrbitInput(context: DebugPilotContext): InputState {
    if (!context.portal.active) {
      return this.computeEdgePatrolInput(context)
    }
    const dx = context.portal.x - context.x
    const dz = context.portal.z - context.z
    const distance = Math.max(1, Math.hypot(dx, dz))
    const angle = Math.atan2(dz, dx)
    const orbitRadius = 980
    const radialError = clamp((distance - orbitRadius) / orbitRadius, -0.55, 0.55)
    const desiredHeading = normalizeAngle(angle + Math.PI / 2 + radialError)
    return {
      desiredHeading,
      boost: false,
      hasAim: true,
      strength: 0.92,
      distancePx: 120,
    }
  }

  private computeEdgePatrolInput(context: DebugPilotContext): InputState {
    this.edgePatrolAngle = normalizeAngle(this.edgePatrolAngle + 0.015)
    const patrolRadius = Math.max(1400, context.fieldRadius * 0.78)
    const targetX = Math.cos(this.edgePatrolAngle) * patrolRadius
    const targetZ = Math.sin(this.edgePatrolAngle) * patrolRadius
    const desiredHeading = Math.atan2(targetZ - context.z, targetX - context.x)
    const distanceFromCenter = Math.hypot(context.x, context.z)
    return {
      desiredHeading,
      boost: distanceFromCenter < patrolRadius * 0.85,
      hasAim: true,
      strength: 0.9,
      distancePx: 110,
    }
  }

  private applySafetyAdjustments(context: DebugPilotContext, input: InputState): InputState {
    const edgeOverride = this.computeEdgeSafetyOverride(context)
    if (edgeOverride) {
      return edgeOverride
    }

    const snakeOverride = this.computeSnakeSafetyOverride(context, input)
    if (snakeOverride) {
      return snakeOverride
    }

    return input
  }

  private computeEdgeSafetyOverride(context: DebugPilotContext): InputState | null {
    const distanceFromCenter = Math.hypot(context.x, context.z)
    const outerDangerRadius = context.fieldRadius * 0.88
    if (distanceFromCenter < outerDangerRadius) {
      return null
    }

    const desiredHeading = Math.atan2(-context.z, -context.x)
    return {
      desiredHeading,
      boost: false,
      hasAim: true,
      strength: 1,
      distancePx: 140,
    }
  }

  private computeSnakeSafetyOverride(context: DebugPilotContext, input: InputState): InputState | null {
    let bestHeading: number | null = null
    let bestDistance = Number.POSITIVE_INFINITY

    for (const snake of context.snakeHeads) {
      const dx = snake.x - context.x
      const dz = snake.z - context.z
      const distance = Math.hypot(dx, dz)
      if (distance <= 0.001 || distance > 1400) continue

      const angleToSnake = Math.atan2(dz, dx)
      const headingDelta = Math.abs(normalizeAngle(angleToSnake - input.desiredHeading))
      if (headingDelta > Math.PI * 0.75 && distance > 700) continue

      if (distance < bestDistance) {
        bestDistance = distance
        const turnSign = normalizeAngle(context.bearing - angleToSnake) >= 0 ? 1 : -1
        bestHeading = normalizeAngle(angleToSnake + turnSign * (Math.PI * 0.68))
      }
    }

    if (bestHeading === null) {
      return null
    }

    return {
      desiredHeading: bestHeading,
      boost: false,
      hasAim: true,
      strength: 1,
      distancePx: bestDistance < 650 ? 185 : 150,
    }
  }

  private getEffectiveMode(): DebugPilotMode {
    if (this.benchmark) {
      return BENCHMARK_PHASES[this.benchmark.phaseIndex]?.mode ?? 'off'
    }
    return this.active ? this.mode : 'off'
  }

  private syncModeButtons(): void {
    const selectedMode = this.benchmark ? BENCHMARK_PHASES[this.benchmark.phaseIndex]?.mode ?? 'off' : this.mode
    for (const [mode, button] of this.modeButtons) {
      const selected = selectedMode === mode && this.benchmark === null
      button.style.opacity = selected ? '1' : '0.78'
      button.style.borderColor = selected ? 'rgba(255, 215, 120, 0.7)' : 'rgba(255,255,255,0.14)'
      button.style.background = selected ? 'rgba(255, 199, 83, 0.18)' : 'rgba(255,255,255,0.06)'
      button.disabled = this.benchmark !== null
    }
  }

  private syncActionButtons(): void {
    this.benchmarkButton.disabled = this.benchmark !== null
    this.stopButton.disabled = this.benchmark === null
  }

  private updateStatus(text: string): void {
    this.statusEl.textContent = text
  }

  private showBadge(text: string, autoHideMs?: number): void {
    if (this.benchmarkCompletionTimer !== null) {
      window.clearTimeout(this.benchmarkCompletionTimer)
      this.benchmarkCompletionTimer = null
    }
    this.benchmarkBadge.style.display = 'block'
    this.benchmarkBadge.textContent = text
    if (autoHideMs && autoHideMs > 0) {
      this.benchmarkCompletionTimer = window.setTimeout(() => {
        this.hideBadge()
      }, autoHideMs)
    }
  }

  private hideBadge(): void {
    if (this.benchmarkCompletionTimer !== null) {
      window.clearTimeout(this.benchmarkCompletionTimer)
      this.benchmarkCompletionTimer = null
    }
    this.benchmarkBadge.style.display = 'none'
  }

  private getButtonCss(primary = false): string {
    return `
      min-height: 32px;
      padding: 6px 10px;
      border-radius: 10px;
      border: 1px solid ${primary ? 'rgba(255, 215, 120, 0.45)' : 'rgba(255,255,255,0.14)'};
      background: ${primary ? 'rgba(255, 199, 83, 0.16)' : 'rgba(255,255,255,0.06)'};
      color: #fff8df;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.02em;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
    `
  }

  private async refreshRuntimeInfo(): Promise<void> {
    try {
      const roomsResp = await fetch('/api/runtime/rooms/economy')
      if (!roomsResp.ok) {
        return
      }

      const roomsJson = await roomsResp.json() as {
        success: boolean
        data?: RuntimeRoomEconomyInfo[]
      }

      const room = roomsJson.data?.find((entry) => entry.roomName === this.options.roomName)
      if (!room) {
        this.infoBadge.style.display = 'none'
        return
      }

      this.infoBadge.textContent = this.buildRuntimeInfoText(room)
      this.infoBadge.style.display = 'block'
    } catch {
      // ignore runtime HUD fetch failures
    }
  }

  private buildRuntimeInfoText(room: RuntimeRoomEconomyInfo): string {
    const control = room.economyDiagnostics.control
    const behavior = control.resolvedBehavior
    const huntPressure = this.describeHuntPressure(behavior.hunterAssignmentShare, behavior.hunterProfile)
    const banking = this.describeBanking(
      behavior.collectorEvacThresholdMult,
      behavior.forcedEvacuationMaxCount,
      behavior.forcedEvacuationMinCarriedSparks,
    )
    const duels = this.describeDuels(behavior.botOnBotHuntShare)

    return [
      `Room: ${control.roomState.toUpperCase()} / ${control.liquidityIntent} (${Math.round(control.freeSparks).toLocaleString()} free / ${Math.round(control.controlSparks).toLocaleString()} room value)`,
      `Bot hunting: ${huntPressure}`,
      `Bot banking: ${banking}`,
      `Bot duels: ${duels}`,
    ].join('\n')
  }

  private describeHuntPressure(hunterAssignmentShare: number, hunterProfile: string): string {
    if (hunterAssignmentShare <= 0.3) return 'softened'
    if (hunterAssignmentShare >= 0.7) {
      return hunterProfile === 'pressure' ? 'hard pressure' : 'high'
    }
    return hunterProfile === 'stalker' ? 'measured' : 'normal'
  }

  private describeBanking(
    collectorEvacThresholdMult: number,
    forcedEvacuationMaxCount: number,
    forcedEvacuationMinCarriedSparks: number,
  ): string {
    if (
      forcedEvacuationMaxCount >= 3 ||
      collectorEvacThresholdMult <= 0.7 ||
      forcedEvacuationMinCarriedSparks <= 80
    ) {
      return 'early portal runs'
    }
    if (collectorEvacThresholdMult >= 1.1 && forcedEvacuationMaxCount <= 0) {
      return 'late portal runs'
    }
    return 'normal'
  }

  private describeDuels(botOnBotHuntShare: number): string {
    if (botOnBotHuntShare <= 0.06) return 'few'
    if (botOnBotHuntShare >= 0.18) return 'many'
    return 'normal'
  }
}
